import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import type { SocialPlatformFilter } from "./SocialPlatformSelector";
import { SocialCustomDates, SocialDateRange } from "./SocialToolbar";
import { Inbox, LayoutDashboard, MapPin, MoreHorizontal, SlidersHorizontal, Tags } from "lucide-react";
import { MobileBottomNav, MobileFab, MobileSheet, SheetOption } from "./mobile/MobileChrome";
import { useViewHistory } from "./mobile/navHistory";
import {
  PRESET_LABELS,
  type RangePreset,
  readStoredRange,
  rangeToQuery,
  resolveRange,
  storeRange,
  todaySantiago,
  type SocialRange,
} from "./socialRange";
import { usePageHeaderSlots } from "../../context/PageHeaderSlotContext";
import { fetchSocialOverview } from "../../api";
import type { SocialDetailPayload, SocialOverviewPayload, GertrudisAuditPayload } from "../../types";
import { SandboxResumen, type SandboxTheme } from "./sandbox/SandboxResumen";
import { SandboxBandeja } from "./sandbox/SandboxBandeja";
import { SandboxLocales } from "./sandbox/SandboxLocales";
import { SandboxTemas } from "./sandbox/SandboxTemas";
import { SocialMensajesTab } from "./mensajes/SocialMensajesTab";
import { SocialYoutubeComentarios } from "./youtube/SocialYoutubeComentarios";
import { useDocumentTheme } from "./useDocumentTheme";
import { useDashboard } from "../../context/DashboardContext";
import {
  byOrigin,
  channelOf,
  countByOrigin,
  fmtInt,
  localKey,
  truthy,
  type BandejaFocus,
  type Origen,
} from "./sandbox/resumenData";
import { SocialFilterSelect } from "./SocialFilterSelect";
import { SocialFilterSelectMobile } from "./mobile/SocialFilterSelectMobile";
import "./sandbox/sandbox.css";
import "./mobile/mobile.css";

const DESKTOP_QUERY = "(min-width: 768px)";
const MONTH_NAMES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function useIsDesktop(): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return matches;
}

function fmtIso(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  const month = MONTH_NAMES[Number(m) - 1];
  if (!y || !month || !d) return iso;
  return `${Number(d)} ${month} ${y}`;
}

function periodLabel(from: string | null | undefined, to: string | null | undefined, days: number): string {
  if (from && to) return `${fmtIso(from)} a ${fmtIso(to)}`;
  if (from) return `Desde ${fmtIso(from)}`;
  if (to) return `Hasta ${fmtIso(to)}`;
  return `Últimos ${days} días`;
}

function shiftIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Días del rango, contando ambos extremos. */
function daysInclusive(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

interface Props {
  data: SocialDetailPayload;
  clientSlug: string;
  onAuditGenerated?: (audit: GertrudisAuditPayload) => void;
  range?: SocialRange;
  onRangeChange?: (range: SocialRange) => void;
  portalControls?: boolean;
  showHeader?: boolean;
}

/**
 * SocialTab (Producción)
 *
 * Jerarquía de filtros y selectores:
 * 1. Canal es transversal a todo (Primer slicer en Desktop y Mobile)
 * 2. Orgánico y Anuncios aplica dentro de Meta (con selección de Red IG/FB)
 * 3. Locales aplica a Google Business Profile
 */
export function SocialTab({
  data,
  clientSlug,
  onAuditGenerated: _onAuditGenerated,
  range: propRange,
  onRangeChange,
  portalControls = true,
  showHeader,
}: Props) {
  // 1. Canal principal (Primer Slicer transversal)
  const [platform, setPlatform] = useState<SocialPlatformFilter>("all");
  // 2. Sub-red dentro de Meta ("all" | "meta_ig" | "meta_fb")
  const [metaNetwork, setMetaNetwork] = useState<"all" | "meta_ig" | "meta_fb">("all");
  // 3. Origen dentro de Meta ("all" | "organic" | "paid")
  const [origen, setOrigen] = useState<Origen>("all");

  const [overview, setOverview] = useState<SocialOverviewPayload | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localRange, setLocalRange] = useState<SocialRange>(readStoredRange);
  type Vista = "resumen" | "bandeja" | "locales" | "temas" | "mensajes" | "youtube";
  const [activeView, setActiveView] = useState<Vista>("resumen");
  const [bandejaFocus, setBandejaFocus] = useState<BandejaFocus | null>(null);
  // Local que la vista Locales abre de entrada en su detalle. null muestra el comparativo.
  const [localesFocusId, setLocalesFocusId] = useState<string | null>(null);
  // Tema que la vista Temas abre de entrada. null deja el primero de la lista.
  const [temasFocusKey, setTemasFocusKey] = useState<string | null>(null);
  const exportRef = useRef<(() => void) | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { session } = useDashboard();
  const showMensajes = !(session?.isClientUser || session?.isClientPortal);
  const range = propRange ?? localRange;
  const theme = useDocumentTheme();
  const headerSlots = usePageHeaderSlots();
  const isDesktop = useIsDesktop();
  const goView = useCallback((v: string) => setActiveView(v as Vista), []);
  useViewHistory(activeView, goView, !isDesktop);

  const changeRange = (next: SocialRange) => {
    if (onRangeChange) {
      onRangeChange(next);
    } else {
      setLocalRange(next);
      storeRange(next);
    }
  };

  const query = useMemo(() => rangeToQuery(range), [range]);
  const queryKey = `${query.desde ?? ""}|${query.hasta ?? ""}`;
  const { desde: tableFrom, hasta: tableTo } = useMemo(() => resolveRange(range), [range]);
  const customIncomplete = range.preset === "custom" && !range.desde && !range.hasta;

  const recentComments = data.recentComments ?? [];
  const googleLocales = useMemo(() => data.locales ?? [], [data.locales]);

  // La vista YouTube es de la agencia: los portales de cliente no la ven.
  const youtubeComments = useMemo(
    () => (showMensajes ? recentComments.filter((c) => channelOf(c) === "youtube") : []),
    [recentComments, showMensajes],
  );
  const hasYoutube = youtubeComments.length > 0;

  // La vista Locales solo tiene sentido con 2 o más fichas de Google
  const hasLocales = useMemo(() => {
    const ids = new Set<string>(googleLocales.map((l) => l.id));
    for (const c of recentComments) {
      if (channelOf(c) !== "gbp") continue;
      const k = localKey(c);
      if (k) ids.add(k);
    }
    return ids.size >= 2;
  }, [recentComments, googleLocales]);

  useEffect(() => {
    if (activeView === "locales" && !hasLocales) {
      setActiveView("resumen");
    }
    if (activeView === "youtube" && !hasYoutube) {
      setActiveView("resumen");
    }
  }, [activeView, hasLocales, hasYoutube]);

  // Comentarios del rango activo sin respuestas propias (base para conteos de canales)
  const rangeCommentsTodos = useMemo(() => {
    if (!recentComments.length) return [];
    return recentComments.filter((c) => {
      if (c.is_owner_author || c.is_reply) return false;
      const d = String(c.date || "").slice(0, 10);
      if (!d) return false;
      if (tableFrom && d < tableFrom) return false;
      if (tableTo && d > tableTo) return false;
      return true;
    });
  }, [recentComments, tableFrom, tableTo]);

  // Conteos por canal en el período
  const channelCounts = useMemo(() => {
    let gbp = 0;
    let metaIg = 0;
    let metaFb = 0;
    let yt = 0;
    for (const c of rangeCommentsTodos) {
      const ch = channelOf(c);
      if (ch === "gbp") gbp++;
      else if (ch === "meta_ig") metaIg++;
      else if (ch === "meta_fb") metaFb++;
      else if (ch === "youtube") yt++;
    }
    return {
      all: rangeCommentsTodos.length,
      gbp,
      meta: metaIg + metaFb,
      metaIg,
      metaFb,
      youtube: yt,
    };
  }, [rangeCommentsTodos]);

  const hasGbp = channelCounts.gbp > 0 || googleLocales.length > 0;
  const hasMetaIg = channelCounts.metaIg > 0;
  const hasMetaFb = channelCounts.metaFb > 0;
  const hasMeta = hasMetaIg || hasMetaFb;
  const hasYoutubeChannel = channelCounts.youtube > 0 && showMensajes;

  // Opciones del Primer Slicer (Canales)
  const channelOptions = useMemo(() => {
    const list: Array<{ id: SocialPlatformFilter; label: string; count: number }> = [];
    const totalFamilies = (hasGbp ? 1 : 0) + (hasMeta ? 1 : 0) + (hasYoutubeChannel ? 1 : 0);
    if (totalFamilies > 1) {
      list.push({ id: "all", label: "Todos los canales", count: channelCounts.all });
    }
    if (hasGbp) {
      list.push({ id: "gbp", label: "Google Business Profile", count: channelCounts.gbp });
    }
    if (hasMeta) {
      const metaLabel = hasMetaIg && hasMetaFb ? "Meta" : hasMetaIg ? "Instagram" : "Facebook";
      list.push({ id: "meta", label: metaLabel, count: channelCounts.meta });
    }
    if (hasYoutubeChannel) {
      list.push({ id: "youtube", label: "YouTube", count: channelCounts.youtube });
    }
    return list;
  }, [hasGbp, hasMeta, hasMetaIg, hasMetaFb, hasYoutubeChannel, channelCounts]);

  const metaRedOptions = useMemo(() => [
    { id: "all" as const, label: "Ambas", count: channelCounts.meta },
    { id: "meta_ig" as const, label: "Instagram", count: channelCounts.metaIg },
    { id: "meta_fb" as const, label: "Facebook", count: channelCounts.metaFb },
  ], [channelCounts]);

  const handlePlatformChange = (next: SocialPlatformFilter) => {
    setPlatform(next);
    if (next !== "meta") {
      setOrigen("all");
      setMetaNetwork("all");
    }
  };

  // Comentarios de Meta para alimentar los conteos de origen (Orgánico vs Anuncios)
  const metaCommentsForCounts = useMemo(() => {
    let rows = rangeCommentsTodos.filter((c) => channelOf(c) === "meta_ig" || channelOf(c) === "meta_fb");
    if (metaNetwork === "meta_ig") rows = rows.filter((c) => channelOf(c) === "meta_ig");
    if (metaNetwork === "meta_fb") rows = rows.filter((c) => channelOf(c) === "meta_fb");
    return rows;
  }, [rangeCommentsTodos, metaNetwork]);

  const metaOrigenCounts = useMemo(() => countByOrigin(metaCommentsForCounts), [metaCommentsForCounts]);
  const origenOptions = useMemo(
    () => [
      { id: "all" as Origen, label: "Todo", count: metaOrigenCounts.all },
      { id: "organic" as Origen, label: "Orgánico", count: metaOrigenCounts.organic },
      { id: "paid" as Origen, label: "Anuncios", count: metaOrigenCounts.paid },
    ],
    [metaOrigenCounts],
  );

  // Filtrado por canal
  const channelComments = useMemo(() => {
    if (platform === "all") return rangeCommentsTodos;
    if (platform === "gbp") return rangeCommentsTodos.filter((c) => channelOf(c) === "gbp");
    if (platform === "meta") {
      if (metaNetwork === "meta_ig") return rangeCommentsTodos.filter((c) => channelOf(c) === "meta_ig");
      if (metaNetwork === "meta_fb") return rangeCommentsTodos.filter((c) => channelOf(c) === "meta_fb");
      return rangeCommentsTodos.filter((c) => channelOf(c) === "meta_ig" || channelOf(c) === "meta_fb");
    }
    if (platform === "youtube") return rangeCommentsTodos.filter((c) => channelOf(c) === "youtube");
    return rangeCommentsTodos;
  }, [rangeCommentsTodos, platform, metaNetwork]);

  // Origen solo aplica dentro de Meta
  const effectiveOrigen = platform === "meta" ? origen : "all";
  const rangeComments = useMemo(() => byOrigin(channelComments, effectiveOrigen), [channelComments, effectiveOrigen]);

  // Plataforma que se solicita al backend de Overview
  const effectiveQueryPlatform = useMemo(() => {
    if (platform === "meta") {
      if (metaNetwork === "meta_ig") return "meta_ig";
      if (metaNetwork === "meta_fb") return "meta_fb";
      return "meta";
    }
    return platform;
  }, [platform, metaNetwork]);

  useEffect(() => {
    let active = true;
    setLoadingOverview(true);
    setError(null);

    fetchSocialOverview(clientSlug, effectiveQueryPlatform, query, effectiveOrigen)
      .then((res) => {
        if (active) setOverview(res);
      })
      .catch((err) => {
        if (active) {
          console.error("Error al cargar datos del tab Social:", err);
          setError(err instanceof Error ? err.message : "Error cargando métricas de Social.");
        }
      })
      .finally(() => {
        if (active) setLoadingOverview(false);
      });

    return () => {
      active = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSlug, effectiveQueryPlatform, queryKey, effectiveOrigen]);

  // Mantiene el indicador de carga visible al menos medio segundo tras cambiar fechas o canal
  const [switching, setSwitching] = useState(false);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setSwitching(true);
    const t = setTimeout(() => setSwitching(false), 600);
    return () => clearTimeout(t);
  }, [queryKey, effectiveQueryPlatform, effectiveOrigen]);

  const scopedRecent = useMemo(() => {
    let rows = recentComments;
    if (platform === "gbp") rows = rows.filter((c) => channelOf(c) === "gbp");
    else if (platform === "meta") {
      if (metaNetwork === "meta_ig") rows = rows.filter((c) => channelOf(c) === "meta_ig");
      else if (metaNetwork === "meta_fb") rows = rows.filter((c) => channelOf(c) === "meta_fb");
      else rows = rows.filter((c) => channelOf(c) === "meta_ig" || channelOf(c) === "meta_fb");
    } else if (platform === "youtube") rows = rows.filter((c) => channelOf(c) === "youtube");

    if (platform === "meta") {
      return byOrigin(rows, origen);
    }
    return rows;
  }, [recentComments, platform, metaNetwork, origen]);

  // Críticos del rango en la vista filtrada, y en todos los canales para el aviso de la navegación
  const urgentCount = useMemo(() => rangeComments.filter((c) => truthy(c.critico)).length, [rangeComments]);
  const urgentTotal = useMemo(() => rangeCommentsTodos.filter((c) => truthy(c.critico)).length, [rangeCommentsTodos]);

  // Locales solo trabaja con Google, sin importar el filtro de canal
  const gbpAll = useMemo(() => recentComments.filter((c) => channelOf(c) === "gbp"), [recentComments]);
  const gbpRange = useMemo(() => rangeCommentsTodos.filter((c) => channelOf(c) === "gbp"), [rangeCommentsTodos]);
  const gbpUrgent = useMemo(() => gbpRange.filter((c) => truthy(c.critico)).length, [gbpRange]);

  // Período anterior de igual largo
  const prevWindow = useMemo(() => {
    if (!tableFrom) return null;
    const to = tableTo ?? todaySantiago();
    const len = daysInclusive(tableFrom, to);
    return { from: shiftIso(tableFrom, -len), to: shiftIso(tableFrom, -1), days: len };
  }, [tableFrom, tableTo]);

  const inPrevWindow = useCallback(
    (c: (typeof recentComments)[number]) => {
      if (!prevWindow || c.is_owner_author || c.is_reply) return false;
      const d = String(c.date || "").slice(0, 10);
      return Boolean(d) && d >= prevWindow.from && d <= prevWindow.to;
    },
    [prevWindow],
  );
  const prevComments = useMemo(
    () => (prevWindow ? scopedRecent.filter(inPrevWindow) : null),
    [scopedRecent, prevWindow, inPrevWindow],
  );
  const gbpPrev = useMemo(() => (prevWindow ? gbpAll.filter(inPrevWindow) : null), [gbpAll, prevWindow, inPrevWindow]);

  const effectiveShowHeader = showHeader !== undefined ? showHeader : !portalControls;

  const sinComentarios =
    platform === "meta" && origen !== "all" && rangeComments.length === 0
      ? `No hay comentarios de ${origen === "paid" ? "anuncios" : "orgánicos"} en este período.`
      : null;
  // El filtro de canal rige Resumen, Bandeja y Temas; Locales, Mensajes y YouTube traen su propio alcance
  const usesSlicers = activeView === "resumen" || activeView === "bandeja" || activeView === "temas";
  const origenAviso = usesSlicers ? sinComentarios : null;

  // Navegación fija: las vistas no cambian con el filtro de canal
  const navPills = (
    <nav className="sbx-nav" aria-label="Secciones" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <button
        type="button"
        className={`sbx-nav-link ${activeView === "resumen" ? "active" : ""}`}
        onClick={() => setActiveView("resumen")}
      >
        Resumen
      </button>
      <button
        type="button"
        className={`sbx-nav-link ${activeView === "bandeja" ? "active" : ""}`}
        onClick={() => openBandeja()}
      >
        Bandeja {urgentTotal > 0 && <span className="sbx-nav-badge">{urgentTotal}</span>}
      </button>
      <button
        type="button"
        className={`sbx-nav-link ${activeView === "temas" ? "active" : ""}`}
        onClick={() => openTemas()}
      >
        Temas
      </button>
      {hasLocales && (
        <button
          type="button"
          className={`sbx-nav-link ${activeView === "locales" ? "active" : ""}`}
          onClick={() => openLocales()}
        >
          Locales
        </button>
      )}
      {showMensajes && (
        <button
          type="button"
          className={`sbx-nav-link ${activeView === "mensajes" ? "active" : ""}`}
          onClick={() => setActiveView("mensajes")}
        >
          Mensajes
        </button>
      )}
      {hasYoutube && (
        <button
          type="button"
          className={`sbx-nav-link ${activeView === "youtube" ? "active" : ""}`}
          onClick={() => setActiveView("youtube")}
        >
          YouTube
        </button>
      )}
    </nav>
  );

  const exportPdf = () => {
    if (exportRef.current) {
      exportRef.current();
      return;
    }
    setActiveView("resumen");
    setTimeout(() => exportRef.current?.(), 250);
  };

  const openBandeja = (focus?: BandejaFocus) => {
    setBandejaFocus(focus ?? null);
    setActiveView("bandeja");
    window.scrollTo({ top: 0 });
  };

  const openComment = (feedbackId: string) => openBandeja({ feedbackId, group: "meta", todos: true });

  const openLocales = (localId?: string) => {
    setMoreOpen(false);
    setLocalesFocusId(localId ?? null);
    setActiveView("locales");
    window.scrollTo({ top: 0 });
  };

  const openTemas = (topic?: string) => {
    setMoreOpen(false);
    setTemasFocusKey(topic ?? null);
    setActiveView("temas");
    window.scrollTo({ top: 0 });
  };

  const exportPdfBtn = (
    <button
      type="button"
      className="sbx-btn-pdf"
      onClick={exportPdf}
    >
      Exportar PDF
    </button>
  );

  const dateControl = (
    <div className="social-topbar-controls">
      <SocialDateRange range={range} onChange={changeRange} />
    </div>
  );

  const channelShort = (opt: { id: SocialPlatformFilter; label: string }) => (opt.id === "gbp" ? "Google" : opt.label);

  // Filtros distintos del valor por defecto, para el resumen con chips que se quitan de a uno
  const appliedFilters = useMemo(() => {
    const list: Array<{ key: string; text: string; clear: () => void }> = [];
    const canal = channelOptions.find((o) => o.id === platform);
    if (platform !== "all" && canal) {
      list.push({ key: "canal", text: `Canal: ${channelShort(canal)}`, clear: () => handlePlatformChange("all") });
    }
    if (platform === "meta" && origen !== "all") {
      const o = origenOptions.find((x) => x.id === origen);
      list.push({ key: "origen", text: `Origen: ${o?.label ?? origen}`, clear: () => setOrigen("all") });
    }
    if (platform === "meta" && metaNetwork !== "all") {
      const r = metaRedOptions.find((x) => x.id === metaNetwork);
      list.push({ key: "red", text: `Red: ${r?.label ?? metaNetwork}`, clear: () => setMetaNetwork("all") });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, origen, metaNetwork, channelOptions, origenOptions, metaRedOptions]);

  // Selector de Canales (Primer Slicer) y Segmentadores Contextuales en Desktop
  const desktopSlicersBar = (
    <div className="sbx-slicers-bar">
      <div className="sbx-slicers-main">
        {channelOptions.length > 1 && (
          <div className="sbx-sub-seg" role="radiogroup" aria-label="Canal">
            {channelOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={platform === opt.id}
                onClick={() => handlePlatformChange(opt.id)}
              >
                {channelShort(opt)} <b>{fmtInt(opt.count)}</b>
              </button>
            ))}
          </div>
        )}

        {platform === "meta" && (
          <>
            <span className="sbx-slicers-sep" aria-hidden="true" />
            <div className="sbx-slicers-group">
              <SocialFilterSelect
                label="Origen"
                value={origen}
                defaultId="all"
                options={origenOptions}
                onChange={setOrigen}
                testId="origen-select"
              />
              {hasMetaIg && hasMetaFb && (
                <SocialFilterSelect label="Red" value={metaNetwork} defaultId="all" options={metaRedOptions} onChange={setMetaNetwork} />
              )}
            </div>
          </>
        )}

        <div className="sbx-slicers-count" role="status">
          <i aria-hidden="true" />
          <span>
            Mostrando: <b>{fmtInt(rangeComments.length)}</b> comentarios
          </span>
          {appliedFilters.length > 0 && (
            <button type="button" className="sbx-link" onClick={() => handlePlatformChange("all")}>
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {appliedFilters.length > 0 && (
        <div className="sbx-applied" aria-label="Filtros aplicados">
          <span className="sbx-applied-label">Filtros aplicados</span>
          {appliedFilters.map((f) => (
            <span key={f.key} className="sbx-applied-chip">
              {f.text}
              <button type="button" aria-label={`Quitar ${f.text}`} onClick={f.clear}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  // Selector de Canales (Primer Slicer) y Segmentadores Contextuales en Mobile
  const mobileSlicersBar = (
    <div className="sbx-slicers-bar-m">
      {/* Nivel 1: Primer Slicer - Canales en móvil */}
      {channelOptions.length > 1 && (
        <div className="sbx-channel-chips-m" role="tablist" aria-label="Canales">
          {channelOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              aria-pressed={platform === opt.id}
              onClick={() => handlePlatformChange(opt.id)}
            >
              <span>{opt.id === "gbp" ? "Google" : opt.label}</span>
              <small>{fmtInt(opt.count)}</small>
            </button>
          ))}
        </div>
      )}

      {/* Nivel 2: Segmentadores dentro de Meta en móvil */}
      {platform === "meta" && (
        <div className="sbx-meta-context-m">
          <SocialFilterSelectMobile
            label="Origen"
            value={origen}
            defaultId="all"
            options={origenOptions}
            onChange={setOrigen}
            testId="origen-select"
          />
          {hasMetaIg && hasMetaFb && (
            <SocialFilterSelectMobile label="Red" value={metaNetwork} defaultId="all" options={metaRedOptions} onChange={setMetaNetwork} />
          )}
        </div>
      )}

    </div>
  );

  return (
    <>
      {portalControls && isDesktop && headerSlots.left && createPortal(navPills, headerSlots.left)}
      {portalControls && isDesktop && headerSlots.actions && createPortal(exportPdfBtn, headerSlots.actions)}
      {portalControls && isDesktop && headerSlots.right && createPortal(dateControl, headerSlots.right)}

      {(!portalControls || !isDesktop || !headerSlots.right) && !propRange && (
        <div className="social-inline-toolbar" style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
          {dateControl}
          {exportPdfBtn}
        </div>
      )}

      {!isDesktop && (
        <>
          {activeView === "resumen" && (
            <MobileFab
              theme={theme}
              label="Filtros"
              badge={range.preset === "MTD" ? "Este mes" : range.preset === "YTD" ? "Este año" : PRESET_LABELS[range.preset]}
              icon={<SlidersHorizontal size={20} aria-hidden="true" />}
              onClick={() => setFiltersOpen(true)}
            />
          )}
          <MobileBottomNav
            theme={theme}
            active={activeView === "mensajes" || activeView === "youtube" ? "mas" : activeView}
            onSelect={(id) => {
              if (id === "mas") {
                setMoreOpen(true);
                return;
              }
              if (id === "temas") return openTemas();
              if (id === "locales") return openLocales();
              if (id === "bandeja") setBandejaFocus(null);
              setActiveView(id as Vista);
              window.scrollTo({ top: 0 });
            }}
            items={[
              { id: "resumen", label: "Resumen", icon: <LayoutDashboard size={20} aria-hidden="true" /> },
              { id: "bandeja", label: "Bandeja", icon: <Inbox size={20} aria-hidden="true" />, badge: urgentTotal },
              { id: "temas", label: "Temas", icon: <Tags size={20} aria-hidden="true" /> },
              ...(hasLocales ? [{ id: "locales", label: "Locales", icon: <MapPin size={20} aria-hidden="true" /> }] : []),
              { id: "mas", label: "Más", icon: <MoreHorizontal size={20} aria-hidden="true" /> },
            ]}
          />
          <MobileSheet
            open={filtersOpen}
            title="Filtros del Resumen"
            theme={theme}
            onClose={() => setFiltersOpen(false)}
            footer={
              <>
                <button type="button" className="sbx-m-action is-quiet" onClick={exportPdf}>
                  Exportar informe PDF
                </button>
                <button type="button" className="sbx-m-action" onClick={() => setFiltersOpen(false)}>
                  Listo
                </button>
              </>
            }
          >
            <div className="sbx-m-group">Período</div>
            <div role="radiogroup" aria-label="Período" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {(Object.keys(PRESET_LABELS) as RangePreset[]).map((key) => (
                <SheetOption
                  key={key}
                  label={PRESET_LABELS[key]}
                  selected={range.preset === key}
                  onClick={() => changeRange(key === "custom" ? { preset: key, desde: range.desde, hasta: range.hasta } : { preset: key })}
                />
              ))}
            </div>
            {range.preset === "custom" && <SocialCustomDates range={range} onChange={changeRange} />}
          </MobileSheet>
          <MobileSheet open={moreOpen} title="Más" theme={theme} onClose={() => setMoreOpen(false)}>
            {showMensajes && (
              <SheetOption
                label="Mensajes"
                selected={activeView === "mensajes"}
                onClick={() => {
                  setMoreOpen(false);
                  setActiveView("mensajes");
                  window.scrollTo({ top: 0 });
                }}
              />
            )}
            {hasYoutube && (
              <SheetOption
                label="YouTube"
                selected={activeView === "youtube"}
                onClick={() => {
                  setMoreOpen(false);
                  setActiveView("youtube");
                  window.scrollTo({ top: 0 });
                }}
              />
            )}
            <div className="sbx-m-group">Cuenta</div>
            <a className="sbx-m-option" href="/auth/logout" style={{ textDecoration: "none" }}>
              Cerrar sesión
            </a>
          </MobileSheet>
        </>
      )}

      {!propRange && range.preset === "custom" && <SocialCustomDates range={range} onChange={changeRange} />}

      {!propRange && customIncomplete && (
        <div className="detail-notice" style={{ padding: "8px 12px", borderRadius: "8px", fontSize: "12px" }}>
          Elige las fechas del rango personalizado.
        </div>
      )}

      {error && (
        <div className="detail-notice is-danger" style={{ marginBottom: "16px", padding: "10px 14px", borderRadius: "8px" }}>
          {error}
        </div>
      )}

      {usesSlicers && (isDesktop ? desktopSlicersBar : mobileSlicersBar)}

      {origenAviso ? (
        <div className="sbx sbx-origen-empty" data-theme={theme} role="status" data-testid="origen-vacio">
          <p>{origenAviso}</p>
          <button type="button" className="sbx-link" onClick={() => setOrigen("all")}>
            Ver todo
          </button>
        </div>
      ) : activeView === "mensajes" && showMensajes ? (
        <SocialMensajesTab clientSlug={clientSlug} theme={theme} />
      ) : activeView === "youtube" && hasYoutube ? (
        <SocialYoutubeComentarios comments={youtubeComments} theme={theme} />
      ) : activeView === "temas" ? (
        <SandboxTemas
          key={temasFocusKey ?? "todos"}
          initialTopic={temasFocusKey}
          theme={theme}
          rangeComments={rangeComments}
          prevComments={prevComments}
          periodLabel={periodLabel(tableFrom, tableTo, overview?.window_days ?? 28)}
          prevLabel={prevWindow ? `${fmtIso(prevWindow.from)} a ${fmtIso(prevWindow.to)}` : ""}
          urgentCount={urgentCount}
          showHeader={effectiveShowHeader}
          onOpenBandeja={openBandeja}
          onNavigateToResumen={() => setActiveView("resumen")}
          onNavigateSection={(sectionId) => {
            setActiveView("resumen");
            setTimeout(() => {
              const el = document.getElementById(sectionId);
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 80);
          }}
          onNavigateToLocales={hasLocales ? () => openLocales() : undefined}
          onExportPdf={exportPdf}
        />
      ) : activeView === "locales" && hasLocales ? (
        <SandboxLocales
          key={localesFocusId ?? "comparativo"}
          initialLocalId={localesFocusId}
          onNavigateToTemas={() => openTemas()}
          theme={theme}
          rangeComments={gbpRange}
          allComments={gbpAll}
          locales={googleLocales}
          prevComments={gbpPrev}
          periodLabel={periodLabel(tableFrom, tableTo, overview?.window_days ?? 28)}
          prevLabel={prevWindow ? `${fmtIso(prevWindow.from)} a ${fmtIso(prevWindow.to)}` : ""}
          urgentCount={gbpUrgent}
          showHeader={effectiveShowHeader}
          onOpenBandeja={openBandeja}
          onNavigateToResumen={() => setActiveView("resumen")}
          onNavigateSection={(sectionId) => {
            setActiveView("resumen");
            setTimeout(() => {
              const el = document.getElementById(sectionId);
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 80);
          }}
          onExportPdf={exportPdf}
        />
      ) : activeView === "resumen" ? (
        <SandboxResumen
          clientSlug={clientSlug}
          overview={overview}
          loading={loadingOverview || switching}
          rangeComments={rangeComments}
          allComments={scopedRecent}
          locales={googleLocales}
          rangeFrom={tableFrom}
          rangeTo={tableTo}
          platform={platform}
          availablePlatforms={channelOptions.map(o => o.id)}
          onPlatformChange={handlePlatformChange}
          periodLabel={periodLabel(tableFrom, tableTo, overview?.window_days ?? 28)}
          theme={theme}
          prevComments={prevComments}
          prevLabel={prevWindow ? `${fmtIso(prevWindow.from)} a ${fmtIso(prevWindow.to)}` : ""}
          onNavigateToBandeja={() => openBandeja()}
          onOpenComment={openComment}
          origen={effectiveOrigen}
          onNavigateToLocales={hasLocales ? openLocales : undefined}
          onNavigateToTemas={openTemas}
          showHeader={effectiveShowHeader}
          exportRef={exportRef}
        />
      ) : (
        <SandboxBandeja
          clientSlug={clientSlug}
          comments={rangeComments}
          allComments={scopedRecent}
          overview={overview}
          loading={loadingOverview}
          theme={theme}
          onNavigateToResumen={() => setActiveView("resumen")}
          onNavigateSection={(sectionId) => {
            setActiveView("resumen");
            setTimeout(() => {
              const el = document.getElementById(sectionId);
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 80);
          }}
          urgentCount={urgentCount}
          showHeader={effectiveShowHeader}
          focus={bandejaFocus ?? undefined}
          onNavigateToLocales={hasLocales ? () => openLocales() : undefined}
          onNavigateToTemas={openTemas}
          platform={platform}
        />
      )}
    </>
  );
}
